import React, { createContext, useContext, useState } from "react";
import { POWERUPS } from "../../shared/powerups";
import PowerUpTokenIcon from "../../shared/PowerUpTokenIcon";
import { flagUrlSquare } from "../../shared/flags";
import Icon from "../setup/Icon";
import { playstylePresentation } from "../setup/presets";
import s from "./RoomDesignsSheet.module.scss";

// /debug/room-designs: sketches for Game Rooms, made with mock players, nothing
// wired. The room is the pre-October layout (players left, the rules in the
// middle, invite and chat right), but every rule is ONE row that says its value;
// pressing it opens a small picker with the old tiles. Then the creation stepper
// (who can join first, then the game) in two takes. Every sketch is shown on PC
// and on a phone held sideways (844 x 390). Press the rows: the pickers work.

// ---------- the rules, as the old room had them ----------
type Key = "map" | "size" | "win" | "speed" | "mode" | "spawn" | "powerups" | "seats" | "fill";
interface Rules { map: string; size: string; winBy: "score" | "rounds"; win: number; speed: string; mode: string; spawn: string; off: string[]; seats: number; fill: string }
const START: Rules = { map: "Stadium", size: "Medium", winBy: "score", win: 10, speed: "Normal", mode: "Classic", spawn: "Normal", off: [], seats: 6, fill: "Medium" };
const MAPS = ["Rectangle", "Stadium", "Square", "Circle"];
const SIZES = ["Small", "Medium", "Large", "Huge"];
const SPEEDS = ["Slow", "Normal", "Fast"];
const MODES = [
  { id: "Classic", line: "All power-ups" }, { id: "Identity crisis", line: "Just annoying ones" }, { id: "Sabotage", line: "Only red power-ups" },
  { id: "Drink and drive", line: "Get a good attorney" }, { id: "Fatboy Slim", line: "Get fat or get thin" },
];
const SPAWNS = ["Off", "Rare", "Normal", "Frequent", "Chaos"];
const FILLS = ["Off", "Easy", "Medium", "Hard"];
const SCORES = [5, 10, 15, 20, 30];
// Every value its own colour, so a glance says what is set: speed slow to fast
// green to red, each map its colour, the two ways to win, spawn rate cool to hot.
const SPEED_C: Record<string, string> = { Slow: "#4caf50", Normal: "#ffd23f", Fast: "#ff5c5c" };
const MAP_C: Record<string, string> = { Rectangle: "#28c4d4", Stadium: "#b06cff", Square: "#ff9f45", Circle: "#ff6ad5" };
const WIN_C = { score: "#ffd23f", rounds: "#4fa8ff" };
const SPAWN_C: Record<string, string> = { Off: "#8ea3bd", Rare: "#4fa8ff", Normal: "#4caf50", Frequent: "#ff9f45", Chaos: "#ff3b5c" };
// the colour a rule's row wears for its current value (undefined: the rule's own)
function valueColor(k: Key, r: Rules): string | undefined {
  if (k === "speed") return SPEED_C[r.speed];
  if (k === "map") return MAP_C[r.map];
  if (k === "win") return WIN_C[r.winBy];
  if (k === "spawn") return SPAWN_C[r.spawn];
  return undefined;
}
const tc = (c?: string) => (c ? ({ "--tc": c } as React.CSSProperties) : undefined);
const ROUNDS = [3, 5, 7, 10, 15];

// ---------- icons (24 grid, 2px strokes) ----------
const sv = { viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 2, strokeLinecap: "round" as const, strokeLinejoin: "round" as const, "aria-hidden": true };
const I = {
  map: () => <svg {...sv}><rect x="3" y="6" width="18" height="12" rx="6" /></svg>,
  win: () => <svg {...sv}><path d="M7 4h10v5a5 5 0 01-10 0V4zM4 5h3M17 5h3M4 5c0 3 1.5 4.5 3 4.5M20 5c0 3-1.5 4.5-3 4.5M12 14v4M8 20h8" /></svg>,
  speed: () => <svg {...sv}><path d="M4 16a8 8 0 1116 0M12 16l4-5" /></svg>,
  mode: () => <svg {...sv}><path d="M4 7h9M17 7h3M4 17h3M11 17h9" /><circle cx="15" cy="7" r="2" /><circle cx="9" cy="17" r="2" /></svg>,
  spawn: () => <svg {...sv}><circle cx="12" cy="13" r="7" /><path d="M12 13V9M10 3h4" /></svg>,
  bolt: () => <svg {...sv}><path d="M13 3L5 13.5h6L10 21l8-10.5h-6L13 3z" /></svg>,
  seats: () => <svg {...sv}><circle cx="9" cy="8" r="3.5" /><path d="M2.5 20a6.5 6.5 0 0113 0M16 4.8a3.5 3.5 0 010 6.4M18 14.6a6.5 6.5 0 013.5 5.4" /></svg>,
  bot: () => <svg {...sv}><rect x="5" y="8" width="14" height="11" rx="3" /><path d="M12 4v4M9 13h.01M15 13h.01" /></svg>,
  chev: () => <svg {...sv}><path d="M9 6l6 6-6 6" /></svg>,
  back: () => <svg {...sv}><path d="M15 6l-6 6 6 6" /></svg>,
  x: () => <svg {...sv}><path d="M6 6l12 12M18 6L6 18" /></svg>,
  globe: () => <svg {...sv}><circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3c3 3.2 3 14.8 0 18M12 3c-3 3.2-3 14.8 0 18" /></svg>,
  lock: () => <svg {...sv}><rect x="5" y="10" width="14" height="10" rx="2" /><path d="M8 10V7a4 4 0 018 0v3" /></svg>,
  keys: () => <svg {...sv}><rect x="2" y="6" width="20" height="12" rx="2" /><path d="M6 10h.01M10 10h.01M14 10h.01M18 10h.01M7 14h10" /></svg>,
  plus: () => <svg {...sv}><path d="M12 5v14M5 12h14" /></svg>,
  copy: () => <svg {...sv}><rect x="8" y="8" width="12" height="12" rx="2" /><path d="M16 8V5a1 1 0 00-1-1H5a1 1 0 00-1 1v10a1 1 0 001 1h3" /></svg>,
  play: () => <svg {...sv}><path d="M7 5l12 7-12 7V5z" fill="currentColor" fillOpacity="0.25" /></svg>,
};
const Shape = ({ map }: { map: string }) => (
  <svg viewBox="0 0 40 24" className={s.shape} aria-hidden="true">
    {map === "Rectangle" ? <rect x="3" y="4" width="34" height="16" rx="1.5" /> : map === "Stadium" ? <rect x="3" y="4" width="34" height="16" rx="8" /> : map === "Square" ? <rect x="11" y="3" width="18" height="18" rx="1.5" /> : <circle cx="20" cy="12" r="9.5" />}
  </svg>
);

// ---------- how a rule reads on its row ----------
const powerupsLine = (r: Rules) => (r.off.length === 0 ? "All 13" : r.off.length >= POWERUPS.length ? "None" : `${POWERUPS.length - r.off.length} of ${POWERUPS.length}`);
const ROWS: { key: Key; label: string; icon: () => React.ReactElement; value: (r: Rules) => string; color: string }[] = [
  { key: "map", label: "Map", icon: I.map, value: (r) => `${r.map} · ${r.size}`, color: "#28c4d4" },
  { key: "win", label: "Winning condition", icon: I.win, value: (r) => (r.winBy === "score" ? `First to ${r.win}` : `${r.win} rounds`), color: "#ffd23f" },
  { key: "speed", label: "Worm speed", icon: I.speed, value: (r) => r.speed, color: "#4fa8ff" },
  { key: "mode", label: "Game mode", icon: I.mode, value: (r) => r.mode, color: "#a78bfa" },
  { key: "spawn", label: "Spawn rate", icon: I.spawn, value: (r) => r.spawn, color: "#ff9f45" },
  { key: "powerups", label: "Power-ups", icon: I.bolt, value: powerupsLine, color: "#19f0b0" },
];
const PLAYER_ROWS: { key: Key; label: string; icon: () => React.ReactElement; value: (r: Rules) => string }[] = [
  { key: "seats", label: "Max players", icon: I.seats, value: (r) => String(r.seats) },
  { key: "fill", label: "Fill empty seats", icon: I.bot, value: (r) => (r.fill === "Off" ? "No" : r.fill) },
];

// ---------- mock players ----------
const PLAYERS = [
  { name: "CurveBoss", color: "#4caf50", flag: "se", keys: ["A", "S"], host: true },
  { name: "Pixel Pete", color: "#03a9f4", flag: "se", keys: ["LEFT", "RIGHT"] },
  { name: "Kurvenkönig", color: "#e91e63", flag: "de", keys: [] as string[] },
];

// Landscape phone frame or not (the sketches size themselves from it).
const Land = createContext(false);

// ---------- the pickers: one rule, its old tiles, in a small modal ----------
function Picker({ k, r, set, close, inline }: { k: Key; r: Rules; set: (p: Partial<Rules>) => void; close: () => void; inline?: boolean }) {
  const land = useContext(Land);
  const row = [...ROWS, ...PLAYER_ROWS].find((x) => x.key === k)!;
  const tiles = (list: string[], cur: string, pick: (v: string) => void, cls = "", colors?: Record<string, string>, color?: string) => (
    <div className={`${s.tiles} ${cls}`}>{list.map((v) => <button key={v} type="button" className={`${s.tile} ${v === cur ? s.on : ""}`} style={tc(colors ? colors[v] : color)} onClick={() => pick(v)}>{v}</button>)}</div>
  );
  let body: React.ReactNode = null;
  if (k === "map") {
    body = (
      <>
        <div className={`${s.tiles} ${s.tiles4}`}>{MAPS.map((m) => <button key={m} type="button" className={`${s.tile} ${s.tileBig} ${m === r.map ? s.on : ""}`} style={tc(MAP_C[m])} onClick={() => set({ map: m })}><Shape map={m} /><span>{m}</span></button>)}</div>
        <small className={s.pickSub}>Size</small>
        {tiles(SIZES, r.size, (v) => set({ size: v }), s.tiles4, undefined, MAP_C[r.map])}
      </>
    );
  } else if (k === "win") {
    body = (
      <>
        <div className={`${s.tiles} ${s.tiles2}`}>
          <button type="button" className={`${s.tile} ${r.winBy === "score" ? s.on : ""}`} style={tc(WIN_C.score)} onClick={() => set({ winBy: "score", win: 10 })}>Score to win</button>
          <button type="button" className={`${s.tile} ${r.winBy === "rounds" ? s.on : ""}`} style={tc(WIN_C.rounds)} onClick={() => set({ winBy: "rounds", win: 5 })}>Rounds to play</button>
        </div>
        <small className={s.pickSub}>{r.winBy === "score" ? "First to" : "Rounds"}</small>
        {tiles((r.winBy === "score" ? SCORES : ROUNDS).map(String), String(r.win), (v) => set({ win: Number(v) }), s.tiles5, undefined, WIN_C[r.winBy])}
      </>
    );
  } else if (k === "speed") body = tiles(SPEEDS, r.speed, (v) => set({ speed: v }), s.tiles3, SPEED_C);
  else if (k === "spawn") body = tiles(SPAWNS, r.spawn, (v) => set({ spawn: v }), s.tiles5, SPAWN_C);
  else if (k === "fill") body = tiles(FILLS, r.fill, (v) => set({ fill: v }), s.tiles4);
  else if (k === "seats") body = tiles(["2", "3", "4", "5", "6", "7", "8"], String(r.seats), (v) => set({ seats: Number(v) }), s.tiles7);
  else if (k === "mode") {
    body = (
      <div className={`${s.tiles} ${land ? s.tiles3 : s.tiles2}`}>
        {MODES.map((m) => {
          const look = playstylePresentation(m.id);
          return (
            <button key={m.id} type="button" className={`${s.modeTile} ${m.id === r.mode ? s.modeOn : ""}`} style={{ "--mc": look.color } as React.CSSProperties} onClick={() => set({ mode: m.id })}>
              <span className={s.modeIcon}><Icon name={look.icon} size={land ? 18 : 22} color={look.color} /></span>
              <span className={s.modeText}><b>{m.id}</b><small>{m.line}</small></span>
            </button>
          );
        })}
      </div>
    );
  } else if (k === "powerups") {
    body = (
      <div className={`${s.tiles} ${s.pus}`}>
        {POWERUPS.map((p) => {
          const off = r.off.indexOf(p.key) !== -1;
          return (
            <button key={p.key} type="button" className={`${s.pu} ${s["pu" + p.color[0].toUpperCase() + p.color.slice(1)]} ${off ? s.puOff : ""}`} onClick={() => set({ off: off ? r.off.filter((x) => x !== p.key) : [...r.off, p.key] })}>
              <PowerUpTokenIcon icon={p.icon} color={p.color} size={land ? 20 : 24} dim={off} /><span>{p.name}</span>
            </button>
          );
        })}
      </div>
    );
  }
  // one press is the choice for a single-value rule; the map, the win and the power-ups take a Done
  const multi = k === "map" || k === "win" || k === "powerups";
  const pickOne = (e: React.MouseEvent) => { e.stopPropagation(); if (!multi && (e.target as HTMLElement).closest("button") && !(e.target as HTMLElement).closest("[data-x]")) setTimeout(close, 120); };
  // under the row that opened it, nothing dimmed (the room's other choices do the same)
  if (inline) return <div className={`${s.oPop} ${s.rowPop}`} onClick={pickOne}>{body}{multi && <button type="button" className={s.done} onClick={close}>Done</button>}</div>;
  return (
    <div className={s.scrim} onClick={close}>
      <div className={`${s.picker} ${k === "powerups" || k === "mode" ? s.pickerWide : ""}`} onClick={(e) => { e.stopPropagation(); if (!multi && (e.target as HTMLElement).closest("button") && !(e.target as HTMLElement).closest("[data-x]")) setTimeout(close, 120); }}>
        <div className={s.pickHead}><span className={s.pickIcon} style={{ color: (row as any).color || "#8ea3bd" }}><row.icon /></span><b>{row.label}</b><button type="button" data-x="" className={s.x} onClick={close} aria-label="Close"><I.x /></button></div>
        {body}
        {multi && <button type="button" className={s.done} onClick={close}>Done</button>}
      </div>
    </div>
  );
}

// ---------- the room's columns ----------
function PlayersCol({ r, open, rows, rich }: { r: Rules; open: (k: Key) => void; rows: boolean; rich?: boolean }) {
  return (
    <div className={`${s.card} ${s.players}`}>
      <div className={s.colHead}><span>Players</span><em>{PLAYERS.length} of {r.seats}</em></div>
      {PLAYERS.map((p) => (
        <div key={p.name} className={s.player} style={{ "--c": p.color } as React.CSSProperties}>
          <i className={s.dot} /><img src={flagUrlSquare(p.flag)} alt="" /><b>{p.name}</b>{p.host && <em>Host</em>}
          <span className={s.keys}>{p.keys.map((k) => <kbd key={k}>{k}</kbd>)}</span>
        </div>
      ))}
      {Array.from({ length: Math.max(0, r.seats - PLAYERS.length) }, (_, i) => <div key={i} className={s.open}>{r.fill === "Off" ? "Open seat" : <>Open seat <em>filled at start</em></>}</div>)}
      <button type="button" className={s.addLine}><I.plus />Add a player on this computer</button>
      {rows && PLAYER_ROWS.map((x) => rich
        ? <button key={x.key} type="button" className={`${s.row} ${s.rich}`} onClick={() => open(x.key)}><span className={s.rowIcon}><x.icon /></span><span className={s.rowLabel}>{x.label}</span><ValueArt k={x.key} r={r} /><span className={s.rowValue}>{x.value(r)}</span><span className={s.rowChev}><I.chev /></span></button>
        : <Row key={x.key} icon={x.icon} label={x.label} value={x.value(r)} onClick={() => open(x.key)} />)}
    </div>
  );
}
function Row({ icon: Ic, label, value, onClick, color, big }: { icon: () => React.ReactElement; label: string; value: string; onClick: () => void; color?: string; big?: boolean }) {
  return (
    <button type="button" className={`${s.row} ${big ? s.rowBig : ""}`} onClick={onClick} style={color ? ({ "--rc": color } as React.CSSProperties) : undefined}>
      <span className={s.rowIcon}><Ic /></span><span className={s.rowLabel}>{label}</span><span className={s.rowValue}>{value}</span><span className={s.rowChev}><I.chev /></span>
    </button>
  );
}
function InviteCol({ kind }: { kind: "Private" | "Public" }) {
  return (
    <div className={s.right}>
      <div className={`${s.card} ${s.invite}`}>
        <div className={s.colHead}><span>Invite</span><em className={s.kind}>{kind === "Private" ? <I.lock /> : <I.globe />}{kind}</em></div>
        <b className={s.code}>KZCG</b>
        <div className={s.link}><span>achtungroyale.io/lobby/KZCG</span><button type="button" className={s.copy} aria-label="Copy link"><I.copy /></button></div>
      </div>
      <div className={`${s.card} ${s.chat}`}><div className={s.colHead}><span>Chat</span></div><p>Say hi to the room.</p><div className={s.chatIn}><span>Message</span><b>Send</b></div></div>
    </div>
  );
}

// ---------- the value, drawn (E, F, G) ----------
const steps = (n: number, of: number, cls: string) => <span className={cls}>{Array.from({ length: of }, (_, k) => <i key={k} className={k < n ? s.lit : ""} />)}</span>;
function ValueArt({ k, r }: { k: Key; r: Rules }) {
  if (k === "map") return <span className={s.vMap}><Shape map={r.map} />{steps(SIZES.indexOf(r.size) + 1, 4, s.vSize)}</span>;
  if (k === "win") return <span className={s.vWin}><small>{r.winBy === "score" ? "first to" : "rounds"}</small><b>{r.win}</b></span>;
  if (k === "speed") return <span className={s.vSpeed}>{Array.from({ length: 3 }, (_, i) => <svg key={i} viewBox="0 0 10 16" className={i <= SPEEDS.indexOf(r.speed) ? s.lit : ""} aria-hidden="true"><path d="M2 2l6 6-6 6" /></svg>)}</span>;
  if (k === "spawn") return steps(SPAWNS.indexOf(r.spawn), 4, s.vBars);
  if (k === "powerups") {
    const on = POWERUPS.filter((p) => r.off.indexOf(p.key) === -1);
    return <span className={s.vPus}>{on.slice(0, 7).map((p) => <PowerUpTokenIcon key={p.key} icon={p.icon} color={p.color} size={18} />)}{on.length > 7 && <em>+{on.length - 7}</em>}{on.length === 0 && <em>none</em>}</span>;
  }
  if (k === "seats") return steps(r.seats, 8, s.vSeats);
  if (k === "fill") return steps(FILLS.indexOf(r.fill), 3, s.vBars);
  return null;
}
function ModeRow({ r, onClick, big }: { r: Rules; onClick: () => void; big?: boolean }) {
  const look = playstylePresentation(r.mode);
  const m = MODES.find((x) => x.id === r.mode);
  return (
    <button type="button" className={`${s.modeRow} ${big ? s.modeRowBig : ""}`} style={{ "--mc": look.color } as React.CSSProperties} onClick={onClick}>
      <span className={s.modeIcon}><Icon name={look.icon} size={big ? 30 : 20} color={look.color} /></span>
      <span className={s.modeText}><small>Game mode</small><b>{r.mode}</b>{m && <em>{m.line}</em>}</span>
      <span className={s.rowChev}><I.chev /></span>
    </button>
  );
}
// E: rows, each value drawn at the right. F: the value is the row's big text with
// its drawing under the name, two to a line. G: the game mode as a wide banner
// in its colour on top, the rest as E's rows.
function RichRow({ x, r, open, tall }: { x: typeof ROWS[number]; r: Rules; open: (k: Key) => void; tall?: boolean }) {
  if (x.key === "mode") return <ModeRow r={r} onClick={() => open("mode")} />;
  return (
    <button type="button" className={`${s.row} ${s.rich} ${tall ? s.richTall : ""}`} onClick={() => open(x.key)} style={{ "--rc": x.color } as React.CSSProperties}>
      <span className={s.rowIcon}><x.icon /></span>
      <span className={s.richText}><span className={s.rowLabel}>{x.label}</span>{tall && <b>{x.value(r)}</b>}</span>
      <ValueArt k={x.key} r={r} />
      {!tall && <span className={s.rowValue}>{x.key === "win" ? "" : x.value(r).replace(/^First to \d+$/, "")}</span>}
      <span className={s.rowChev}><I.chev /></span>
    </button>
  );
}

// The rules: A, a list of rows; B, tiles with the value large; C, the map as a
// picture with rows beneath; D, the rules as one line of chips.
type Variant = "A" | "B" | "C" | "D" | "E" | "F" | "G";
function Rules({ v, r, open }: { v: Variant; r: Rules; open: (k: Key) => void }) {
  if (v === "E") return <div className={s.rows}>{ROWS.map((x) => <RichRow key={x.key} x={x} r={r} open={open} />)}</div>;
  if (v === "F") return <div className={s.richGrid}>{ROWS.map((x) => x.key === "mode" ? <ModeRow key="mode" r={r} onClick={() => open("mode")} /> : <RichRow key={x.key} x={x} r={r} open={open} tall />)}</div>;
  if (v === "G") return <><ModeRow r={r} onClick={() => open("mode")} big /><div className={s.rows}>{ROWS.filter((x) => x.key !== "mode").map((x) => <RichRow key={x.key} x={x} r={r} open={open} />)}</div></>;
  if (v === "B") {
    return (
      <div className={s.grid2}>
        {ROWS.map((x) => (
          <button key={x.key} type="button" className={s.ruleTile} onClick={() => open(x.key)} style={{ "--rc": x.color } as React.CSSProperties}>
            <span className={s.rowIcon}><x.icon /></span><small>{x.label}</small><b>{x.value(r)}</b>
          </button>
        ))}
      </div>
    );
  }
  if (v === "C") {
    return (
      <>
        <button type="button" className={s.mapHero} onClick={() => open("map")}>
          <Shape map={r.map} /><span><small>Map</small><b>{r.map}</b><em>{r.size}</em></span><span className={s.rowChev}><I.chev /></span>
        </button>
        <div className={s.rows}>{ROWS.slice(1).map((x) => <Row key={x.key} icon={x.icon} label={x.label} value={x.value(r)} color={x.color} onClick={() => open(x.key)} />)}</div>
      </>
    );
  }
  if (v === "D") {
    return (
      <div className={s.chipsWrap}>
        <small className={s.colHeadSmall}>Rules</small>
        <div className={s.chips}>{ROWS.map((x) => <button key={x.key} type="button" className={s.chip} style={{ "--rc": x.color } as React.CSSProperties} onClick={() => open(x.key)}><x.icon />{x.value(r)}</button>)}</div>
        <div className={s.dPreview}><Shape map={r.map} /><span>{r.map} · {r.size}</span></div>
      </div>
    );
  }
  return <div className={s.rows}>{ROWS.map((x) => <Row key={x.key} icon={x.icon} label={x.label} value={x.value(r)} color={x.color} big onClick={() => open(x.key)} />)}</div>;
}

function Room({ v }: { v: Variant }) {
  const land = useContext(Land);
  const [r, setR] = useState<Rules>(START);
  const [open, setOpen] = useState<Key | null>(null);
  const [tab, setTab] = useState<"players" | "rules" | "invite">("rules");
  const set = (p: Partial<Rules>) => setR((x) => ({ ...x, ...p }));
  const head = (
    <div className={s.roomHead}>
      <button type="button" className={s.back} aria-label="Back"><I.back /></button>
      <b>CurveBoss's room</b>
    </div>
  );
  const start = <button type="button" className={s.start}><I.play />Start game</button>;
  if (land) {
    // a phone sideways: three tabs on the left (players, rules, invite), the
    // chosen one beside them, Start always there
    return (
      <div className={s.roomLand}>
        <div className={s.landRail}>
          <button type="button" className={s.back} aria-label="Back"><I.back /></button>
          {(["players", "rules", "invite"] as const).map((t) => <button key={t} type="button" className={`${s.landTab} ${tab === t ? s.landOn : ""}`} onClick={() => setTab(t)}>{t === "players" ? <I.seats /> : t === "rules" ? <I.mode /> : <I.globe />}<span>{t[0].toUpperCase() + t.slice(1)}</span></button>)}
        </div>
        <div className={s.landBody}>
          {tab === "players" ? <PlayersCol r={r} open={setOpen} rows rich={v >= "E"} /> : tab === "invite" ? <InviteCol kind="Private" /> : <div className={`${s.card} ${s.mid}`}><Rules v={v} r={r} open={setOpen} /></div>}
          {start}
        </div>
        {open && <Picker k={open} r={r} set={set} close={() => setOpen(null)} />}
      </div>
    );
  }
  return (
    <div className={s.room}>
      {head}
      <div className={s.cols}>
        <PlayersCol r={r} open={setOpen} rows rich={v >= "E"} />
        <div className={s.midCol}>
          <div className={`${s.card} ${s.mid}`}><Rules v={v} r={r} open={setOpen} /></div>
          {start}
        </div>
        <InviteCol kind="Private" />
      </div>
      {open && <Picker k={open} r={r} set={set} close={() => setOpen(null)} />}
    </div>
  );
}

// ---------- H: the old room, the left column simpler ----------
// The room as it was before October (rules all laid out in the middle, invite
// and chat on the right), with the players column cut down to two buttons:
// "Add player" opens a choice (someone on this computer, or a bot of a level),
// "Autofill with Medium bots" opens the four levels (Off, Easy, Medium, Hard).
function Section({ icon, title, color, children }: { icon: string; title: string; color: string; children: React.ReactNode }) {
  return (
    <div className={s.oSec}>
      <div className={s.oSecHead}><span className={s.oSecIcon} style={{ color, background: color + "22" }}><Icon name={icon} size={14} color={color} /></span><b>{title}</b><i /></div>
      {children}
    </div>
  );
}
const KINDS3 = [
  { id: "Private" as const, icon: I.lock, line: "Only people with the code or link" },
  { id: "Public" as const, icon: I.globe, line: "Listed in open rooms, anyone can join" },
  { id: "Local" as const, icon: I.keys, line: "Everyone plays on this device" },
];
// a stand-in QR code (21 x 21: the three corner squares, noise between)
const QR = Array.from({ length: 441 }, (_, i) => {
  const x = i % 21, y = Math.floor(i / 21);
  const finder = (fx: number, fy: number) => { const dx = x - fx, dy = y - fy; if (dx < 0 || dy < 0 || dx > 6 || dy > 6) return null; return dx === 0 || dy === 0 || dx === 6 || dy === 6 || (dx >= 2 && dx <= 4 && dy >= 2 && dy <= 4); };
  const f = finder(0, 0) ?? finder(14, 0) ?? finder(0, 14);
  if (f !== null) return f;
  if ((x < 8 && y < 8) || (x > 12 && y < 8) || (x < 8 && y > 12)) return false;
  return ((x * 73 + y * 151 + x * y * 7) % 11) < 5;
});
// ---------- E1 to E5: E's rows as the old room's middle ----------
type EStyle = "E1" | "E2" | "E3" | "E4" | "E5";
// every option of a rule, the chosen one lit (E5: the choices show without opening)
const OPTIONS: Partial<Record<Key, string[]>> = { speed: SPEEDS, spawn: SPAWNS, size: SIZES };
function Strip({ list, cur, colors }: { list: string[]; cur: string; colors: Record<string, string> }) {
  return <span className={s.strip}>{list.map((v) => <i key={v} className={v === cur ? s.stripOn : ""} style={{ "--sc": colors[v] } as React.CSSProperties}>{v}</i>)}</span>;
}
function ERow({ x, r, st, onClick, open }: { x: typeof ROWS[number]; r: Rules; st: EStyle; onClick: () => void; open: boolean }) {
  const look = playstylePresentation(r.mode);
  const vars = { "--rc": x.key === "mode" ? look.color : valueColor(x.key, r) || x.color } as React.CSSProperties;
  const cls = `${s.eRow} ${s["e" + st]} ${open ? s.eOpen : ""}`;
  const chev = <span className={`${s.rowChev} ${open ? s.oChevDown : ""}`}><I.chev /></span>;
  const icon = x.key === "mode" ? <span className={s.modeIcon} style={{ "--mc": look.color } as React.CSSProperties}><Icon name={look.icon} size={st === "E2" ? 22 : 18} color={look.color} /></span> : <span className={s.rowIcon}><x.icon /></span>;
  const text = x.key === "mode" ? r.mode : x.key === "win" ? (r.winBy === "score" ? "First to" : "Rounds") : x.key === "powerups" ? powerupsLine(r) : x.key === "map" ? r.map : x.value(r);
  const art = x.key === "mode" ? <em className={s.eModeLine}>{(MODES.find((m) => m.id === r.mode) || MODES[0]).line}</em> : <ValueArt k={x.key} r={r} />;
  if (st === "E2") {
    // the label small over the value, the drawing large
    return <button type="button" className={cls} style={vars} onClick={onClick}>{icon}<span className={s.eStack}><small>{x.label}</small><b>{x.key === "win" ? `${text} ${r.win}` : x.key === "map" ? `${r.map} · ${r.size}` : text}</b></span><span className={s.eBigArt}>{art}</span>{chev}</button>;
  }
  if (st === "E4") {
    // the value as a pill in the rule's colour: the drawing and the words together
    return <button type="button" className={cls} style={vars} onClick={onClick}>{icon}<span className={s.rowLabel}>{x.label}</span><span className={s.ePill}>{art}<b>{x.key === "win" ? "" : text}</b></span>{chev}</button>;
  }
  if (st === "E5" && (x.key === "speed" || x.key === "spawn")) {
    return <button type="button" className={cls} style={vars} onClick={onClick}>{icon}<span className={s.rowLabel}>{x.label}</span><Strip list={OPTIONS[x.key]!} cur={x.key === "speed" ? r.speed : r.spawn} colors={x.key === "speed" ? SPEED_C : SPAWN_C} />{chev}</button>;
  }
  return <button type="button" className={cls} style={vars} onClick={onClick}>{icon}<span className={s.rowLabel}>{x.label}</span>{art}<span className={s.rowValue}>{x.key === "win" ? "" : text}</span>{chev}</button>;
}
function EMiddle({ st, r, set }: { st: EStyle; r: Rules; set: (p: Partial<Rules>) => void }) {
  const [open, setOpen] = useState<Key | null>(null);
  const row = (x: typeof ROWS[number]) => (
    <div key={x.key} className={s.oAnchor}>
      <ERow x={x} r={r} st={st} open={open === x.key} onClick={() => setOpen(open === x.key ? null : x.key)} />
      {open === x.key && <Picker k={x.key} r={r} set={set} close={() => setOpen(null)} inline />}
    </div>
  );
  const catcher = open && <div className={s.oCatch} onClick={() => setOpen(null)} />;
  if (st === "E3") {
    // two groups under the old section headings: the arena, then the power-ups
    const g1 = ROWS.filter((x) => x.key === "map" || x.key === "win" || x.key === "speed");
    const g2 = ROWS.filter((x) => x.key === "mode" || x.key === "spawn" || x.key === "powerups");
    return <div className={s.eList}>{catcher}<Section icon="map" title="Arena" color="#28c4d4"><div className={s.eGroup}>{g1.map(row)}</div></Section><Section icon="bolt" title="Power-ups" color="#4caf50"><div className={s.eGroup}>{g2.map(row)}</div></Section></div>;
  }
  return <div className={`${s.eList} ${st === "E2" ? s.eListBig : ""}`}>{catcher}{ROWS.map(row)}</div>;
}

function OldRoom({ eStyle }: { eStyle?: EStyle } = {}) {
  const land = useContext(Land);
  const [r, setR] = useState<Rules>(START);
  const set = (p: Partial<Rules>) => setR((x) => ({ ...x, ...p }));
  const [modal, setModal] = useState<null | "add" | "fill" | "kind">(null);
  const [kind, setKind] = useState<"Private" | "Public" | "Local">("Private");
  const [extra, setExtra] = useState<{ name: string; color: string; bot?: string }[]>([]);
  const [tab, setTab] = useState<"players" | "rules" | "invite">("rules");
  const opts = (list: string[], cur: string, pick: (v: string) => void, cols: number, accent?: string) => (
    <div className={s.oOpts} style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>{list.map((v) => <button key={v} type="button" className={`${s.oOpt} ${v === cur ? s.oOn : ""}`} style={accent && v === cur ? ({ "--oc": accent } as React.CSSProperties) : undefined} onClick={() => pick(v)}>{v}</button>)}</div>
  );
  const players: { name: string; color: string; bot?: string }[] = [...PLAYERS.slice(0, 1), ...extra];
  const Left = () => (
    <div className={s.oLeft}>
      <div className={`${s.card} ${s.oCard}`}>
        <div className={s.oMax}><b>Max players</b><button type="button" className={s.oStep} onClick={() => set({ seats: Math.max(2, r.seats - 1) })}>−</button><span>{r.seats}</span><button type="button" className={s.oStep} onClick={() => set({ seats: Math.min(8, r.seats + 1) })}>+</button></div>
        <div className={s.oAnchor}>
          <button type="button" className={`${s.oAdd} ${modal === "add" ? s.oOpenBtn : ""}`} onClick={() => setModal(modal === "add" ? null : "add")}><I.plus />Add player</button>
          {modal === "add" && popover}
        </div>

      </div>
      <div className={`${s.card} ${s.oCard} ${s.oRoster}`}>
        <div className={s.colHead}><span>Players ({players.length}/{r.seats})</span></div>
        {players.map((p, i) => (
          <div key={i} className={s.oPlayer}><i style={{ background: p.color }} /><b>{p.name}</b>{i === 0 && <em>· host</em>}{p.bot ? <span className={s.oBot}>{p.bot}</span> : <span className={s.keys}><kbd>{i === 0 ? "LEFT" : "A"}</kbd><kbd>{i === 0 ? "RIGHT" : "S"}</kbd></span>}{i > 0 && <button type="button" className={s.oRemove} onClick={() => setExtra((x) => x.filter((_, k) => k !== i - 1))} aria-label="Remove"><I.x /></button>}</div>
        ))}
        {Array.from({ length: Math.max(0, r.seats - players.length) }, (_, i) => <div key={i} className={s.oSeat}>+ Open seat{r.fill !== "Off" && <span><em>auto-bot</em><span className={s.oBot}>{r.fill}</span></span>}</div>)}
      </div>
    </div>
  );
  const mid = (
    <div className={`${s.card} ${s.oMid}`}>
      {eStyle ? <div className={s.oMidScroll}><EMiddle st={eStyle} r={r} set={set} /></div> : <div className={s.oMidScroll}>
        <Section icon="map" title="Map" color="#28c4d4">
          <div className={s.oOpts} style={{ gridTemplateColumns: "repeat(4, minmax(0, 1fr))" }}>{MAPS.map((m) => <button key={m} type="button" className={`${s.oOpt} ${s.oMap} ${m === r.map ? s.oOn : ""}`} onClick={() => set({ map: m })}><Shape map={m} /><span>{m}</span></button>)}</div>
          {opts(SIZES, r.size, (v) => set({ size: v }), 4)}
        </Section>
        <Section icon="trophy" title="Winning condition" color="#f5c542">
          <div className={s.oOpts} style={{ gridTemplateColumns: "1fr 1fr" }}>
            {(["score", "rounds"] as const).map((w) => (
              <button key={w} type="button" className={`${s.oWin} ${r.winBy === w ? s.oOn : ""}`} onClick={() => set({ winBy: w })}>
                <span><i className={s.oRadio} />{w === "score" ? "Score to win" : "Rounds to play"}<b>{r.win}</b></span>
                <span className={s.oSlider}><i style={{ width: `${(r.win / 30) * 100}%` }} /></span>
              </button>
            ))}
          </div>
        </Section>
        <Section icon="gauge" title="Worm speed" color="#28c4d4">{opts(SPEEDS, r.speed, (v) => set({ speed: v }), 3, SPEED_C[r.speed])}</Section>
        <Section icon="sliders" title="Game mode" color="#b06cff">
          <div className={s.oOpts} style={{ gridTemplateColumns: `repeat(${land ? 3 : 5}, minmax(0, 1fr))` }}>
            {MODES.map((m) => { const look = playstylePresentation(m.id); return (
              <button key={m.id} type="button" className={`${s.oOpt} ${s.oMode} ${m.id === r.mode ? s.oOn : ""}`} style={{ "--oc": look.color } as React.CSSProperties} onClick={() => set({ mode: m.id })}>
                <span className={s.oModeIcon} style={{ background: look.color + "26" }}><Icon name={look.icon} size={14} color={look.color} /></span><span>{m.id}</span>
              </button>
            ); })}
          </div>
        </Section>
        <Section icon="timer" title="Spawn rate" color="#ff9f45">{opts(SPAWNS, r.spawn, (v) => set({ spawn: v }), 5, SPAWN_C[r.spawn])}</Section>
        <Section icon="bolt" title="Power-ups" color="#4caf50">
          <div className={s.oOpts} style={{ gridTemplateColumns: `repeat(${land ? 4 : 5}, minmax(0, 1fr))` }}>
            {POWERUPS.map((p) => { const off = r.off.indexOf(p.key) !== -1; return (
              <button key={p.key} type="button" className={`${s.pu} ${s["pu" + p.color[0].toUpperCase() + p.color.slice(1)]} ${off ? s.puOff : ""}`} onClick={() => set({ off: off ? r.off.filter((x) => x !== p.key) : [...r.off, p.key] })}><PowerUpTokenIcon icon={p.icon} color={p.color} size={land ? 18 : 22} dim={off} /><span>{p.name}</span></button>
            ); })}
          </div>
        </Section>
      </div>}
      <button type="button" className={s.oStart}>Start game</button>
    </div>
  );
  const right = (
    <div className={s.oRight}>
      <div className={`${s.card} ${s.oCard} ${s.oInvite}`}>
        <div className={s.colHead}><span>Invite</span></div>
        <div className={s.oQr} aria-hidden="true">{QR.map((on, i) => <i key={i} className={on ? s.oQrOn : ""} />)}</div>
        <b className={s.oCode}>J548 <I.copy /></b>
        <small className={s.oShareLabel}>Share link</small>
        <div className={s.link}><span>achtungroyale.io/lobby/J548</span><button type="button" className={s.copy} aria-label="Copy link"><I.copy /></button></div>
      </div>
      <div className={`${s.card} ${s.chat}`}><div className={s.colHead}><span>Chat</span></div><p>Say hi to the lobby.</p><div className={s.chatIn}><span>Message</span><b>Send</b></div></div>
    </div>
  );
  // The choice drops down right under the button that opened it; nothing is
  // dimmed. A press anywhere else closes it (the clear catcher under it).
  // One drop-down for the whole roster: someone on this computer, a bot now,
  // and what fills the empty seats at the start. It stays open while you add
  // (several bots in a row); the cross or a press outside closes it.
  const popover = modal === "add" && (
    <div className={s.oPop} onClick={(e) => e.stopPropagation()}>
      <button type="button" className={`${s.x} ${s.oPopX}`} onClick={() => setModal(null)} aria-label="Close"><I.x /></button>
      <button type="button" className={s.oChoice} onClick={() => setExtra((x) => [...x, { name: "Pixel Pete", color: "#03a9f4" }])}>
        <span className={s.kindIcon}><I.keys /></span><span><b>Player on this computer</b><small>Steers with A / S</small></span>
      </button>
      <small className={s.pickSub}>Or a bot</small>
      <div className={`${s.tiles} ${s.tiles3}`}>{["Easy", "Medium", "Hard"].map((lvl) => <button key={lvl} type="button" className={s.tile} onClick={() => setExtra((x) => [...x, { name: lvl === "Easy" ? "Zigzag Zoe" : lvl === "Medium" ? "Loop Lou" : "Hairpin Hank", color: lvl === "Easy" ? "#e91e63" : lvl === "Medium" ? "#ff9800" : "#9c27b0", bot: lvl }])}><I.bot />{lvl}</button>)}</div>
      <small className={s.pickSub}>Autofill</small>
      <div className={`${s.tiles} ${s.tiles4} ${s.oFills}`}>{FILLS.map((f) => <button key={f} type="button" className={`${s.tile} ${f === r.fill ? s.on : ""}`} style={tc("#f5c542")} onClick={() => set({ fill: f })}>{f === "Off" ? "No" : f}</button>)}</div>
    </div>
  );
  const modals = modal && <div className={s.oCatch} onClick={() => setModal(null)} />;
  if (land) {
    return (
      <div className={s.roomLand}>
        <div className={s.landRail}>
          <button type="button" className={s.back} aria-label="Back"><I.back /></button>
          {(["players", "rules", "invite"] as const).map((t) => <button key={t} type="button" className={`${s.landTab} ${tab === t ? s.landOn : ""}`} onClick={() => setTab(t)}>{t === "players" ? <I.seats /> : t === "rules" ? <I.mode /> : <I.globe />}<span>{t[0].toUpperCase() + t.slice(1)}</span></button>)}
        </div>
        <div className={`${s.landBody} ${s.oLand}`}>{tab === "players" ? <Left /> : tab === "invite" ? right : mid}</div>
        {modals}
      </div>
    );
  }
  return (
    <div className={s.oRoom}>
      <Left />
      <div className={s.oCenter}>
        <div className={s.oHead}>
          <button type="button" className={s.back} aria-label="Back"><I.back /></button><b>New Lobby</b>
          {/* who can join, changed in place: a drop-down like the left column's */}
          <div className={s.oAnchor}>
            <button type="button" className={`${s.oKindBtn} ${s["oKind" + kind]} ${modal === "kind" ? s.oOpenBtn : ""}`} onClick={() => setModal(modal === "kind" ? null : "kind")}>
              {kind === "Private" ? <I.lock /> : kind === "Public" ? <I.globe /> : <I.keys />}{kind}<span className={`${s.rowChev} ${s.oChevDown}`}><I.chev /></span>
            </button>
            {modal === "kind" && (
              <div className={`${s.oPop} ${s.oKindPop}`} onClick={(e) => e.stopPropagation()}>
                {KINDS3.map((k) => (
                  <button key={k.id} type="button" className={`${s.oKindOpt} ${kind === k.id ? s.oKindOn : ""}`} onClick={() => { setKind(k.id); setModal(null); }}>
                    <span className={s.kindIcon}><k.icon /></span><span><b>{k.id}</b><small>{k.line}</small></span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        {mid}
      </div>
      {right}
      {modals}
    </div>
  );
}

// ---------- creating a room: a stepper in a modal ----------
// S1: two steps (who can join, then the game: a few presets or your own rules);
// the room opens after the second. S2: one step with the kind as three tabs on
// top and the rules as rows under it (the same rows as the room), then Create.
function Stepper({ v }: { v: "S1" | "S2" | "S3" }) {
  const land = useContext(Land);
  const [step, setStep] = useState(0);
  const [kind, setKind] = useState<string | null>(null);
  const [preset, setPreset] = useState("Classic");
  const [r, setR] = useState<Rules>(START);
  const [open, setOpen] = useState<Key | null>(null);
  const KINDS = [
    { id: "Public", icon: I.globe, line: "Listed in open rooms, anyone can join" },
    { id: "Private", icon: I.lock, line: "Only people with the code or link" },
    { id: "Local", icon: I.keys, line: "Everyone plays on this device" },
  ];
  const dots = (n: number) => <span className={s.dots}>{[0, 1].map((k) => <i key={k} className={k <= n ? s.dotOn : ""} />)}</span>;
  let body: React.ReactNode;
  if (v === "S3") {
    // the one choice: who can join. Pressing a card creates the room.
    body = (
      <>
        <div className={s.stepHead}><b>New room</b></div>
        <div className={`${s.kinds3} ${land ? s.kinds3Land : ""}`}>
          {KINDS.map((k) => (
            <button key={k.id} type="button" className={s.kindBig}>
              <span className={s.kindBigIcon}><k.icon /></span><b>{k.id}</b><small>{k.line}</small>
            </button>
          ))}
        </div>
      </>
    );
  } else if (v === "S1" && step === 0) {
    body = (
      <>
        <div className={s.stepHead}>{dots(0)}<b>Who can join?</b></div>
        <div className={`${s.kinds} ${land ? s.kindsRow : ""}`}>
          {KINDS.map((k) => <button key={k.id} type="button" className={`${s.kindCard} ${kind === k.id ? s.on : ""}`} onClick={() => { setKind(k.id); setStep(1); }}><span className={s.kindIcon}><k.icon /></span><b>{k.id}</b><small>{k.line}</small></button>)}
        </div>
      </>
    );
  } else if (v === "S1") {
    body = (
      <>
        <div className={s.stepHead}><button type="button" className={s.back} onClick={() => setStep(0)} aria-label="Back"><I.back /></button>{dots(1)}<b>The game</b><em>{kind} room</em></div>
        <div className={`${s.presets} ${land ? s.presetsLand : ""}`}>
          {["Classic", "Pure skill", "Chaos", "Quick duel", "Your own rules"].map((p) => <button key={p} type="button" className={`${s.tile} ${s.tileText} ${preset === p ? s.on : ""}`} onClick={() => setPreset(p)}><b>{p}</b><span>{p === "Classic" ? "All power-ups, first to 10" : p === "Pure skill" ? "No power-ups" : p === "Chaos" ? "Power-ups everywhere" : p === "Quick duel" ? "2 players, first to 3" : "Set every rule in the room"}</span></button>)}
        </div>
        <button type="button" className={s.done}>Create room</button>
      </>
    );
  } else {
    body = (
      <>
        <div className={s.stepHead}><b>New room</b></div>
        <div className={s.kindTabs}>{KINDS.map((k) => <button key={k.id} type="button" className={`${s.kindTab} ${(kind || "Private") === k.id ? s.on : ""}`} onClick={() => setKind(k.id)}><k.icon />{k.id}</button>)}</div>
        <div className={`${s.rows} ${land ? s.rowsTwo : ""}`}>{ROWS.map((x) => <Row key={x.key} icon={x.icon} label={x.label} value={x.value(r)} color={x.color} onClick={() => setOpen(x.key)} />)}</div>
        <button type="button" className={s.done}>Create room</button>
      </>
    );
  }
  return (
    <div className={s.stepStage}>
      <div className={`${s.stepper} ${v === "S2" ? s.stepperWide : ""} ${v === "S3" ? s.stepperS3 : ""}`}>
        <button type="button" className={`${s.x} ${s.stepX}`} aria-label="Close"><I.x /></button>
        {body}
      </div>
      {open && <Picker k={open} r={r} set={(p) => setR((x) => ({ ...x, ...p }))} close={() => setOpen(null)} />}
    </div>
  );
}

// ---------- how a map's size could be drawn (the Map row's drawing) ----------
// n: 1 small .. 4 huge. Each design in the row's colour, on the row as it is.
const SIZE_NAMES = ["Small", "Medium", "Large", "Huge"];
function SizeArt({ d, n }: { d: number; n: number }) {
  const lit = "currentColor", off = "rgba(255,255,255,0.14)";
  if (d === 1) return ( // nested outlines, lit up to the size
    <svg viewBox="0 0 30 30" width="30" height="30" fill="none">{[26, 19, 12, 5].map((w, i) => <rect key={w} x={15 - w / 2} y={15 - w / 2} width={w} height={w} rx={Math.min(3, w / 4)} stroke={4 - i <= n ? lit : off} strokeWidth="1.6" />)}</svg>
  );
  if (d === 2) return ( // corner brackets, further apart the bigger it is
    <svg viewBox="0 0 30 30" width="30" height="30" fill="none" stroke={lit} strokeWidth="2" strokeLinecap="round">
      {(() => { const g = 3 + n * 2.6, a = 15 - g, b = 15 + g, k = 4; return <path d={`M${a} ${a + k}V${a}h${k}M${b - k} ${a}h${k}v${k}M${b} ${b - k}v${k}h${-k}M${a + k} ${b}h${-k}v${-k}`} />; })()}
      <rect x="13" y="13" width="4" height="4" rx="1" fill={lit} stroke="none" />
    </svg>
  );
  if (d === 3) return ( // four dots growing, lit up to the size (S M L XL)
    <svg viewBox="0 0 44 20" width="44" height="20">{[3, 4.5, 6.2, 8].map((rr, i) => <circle key={i} cx={[4, 12.5, 23, 35][i]} cy={12 - rr + 6} r={rr} fill={i < n ? lit : off} />)}</svg>
  );
  if (d === 4) return ( // the arena drawn to its size inside a fixed frame
    <svg viewBox="0 0 40 24" width="40" height="24" fill="none">
      <rect x="1" y="1" width="38" height="22" rx="3" stroke={off} strokeWidth="1.2" strokeDasharray="2 2" />
      {(() => { const f = 0.35 + n * 0.16, w = 36 * f, h = 20 * f; return <rect x={20 - w / 2} y={12 - h / 2} width={w} height={h} rx={h / 2} stroke={lit} strokeWidth="1.8" fill={lit} fillOpacity="0.15" />; })()}
    </svg>
  );
  // 5: the size as a letter, in a tag
  return <span style={{ display: "inline-grid", placeItems: "center", minWidth: 30, height: 24, padding: "0 6px", borderRadius: 5, border: "1.5px solid currentColor", font: "700 12px 'Chakra Petch', sans-serif", letterSpacing: 1 }}>{["S", "M", "L", "XL"][n - 1]}</span>;
}
const SIZE_DESIGNS = [
  { d: 1, name: "Nested outlines", line: "four squares, small inside to huge outside; lit up to the size" },
  { d: 2, name: "Corner brackets", line: "a frame's corners, further apart the bigger the map" },
  { d: 3, name: "Growing dots", line: "four dots from small to large, lit up to the size" },
  { d: 4, name: "Arena in a frame", line: "the arena itself, drawn to its size in a fixed dashed frame" },
  { d: 5, name: "Size letter", line: "S, M, L or XL in a small tag" },
];
function SizeLab() {
  return (
    <div className={s.sizeLab}>
      {SIZE_DESIGNS.map((x) => (
        <div key={x.d} className={s.sizeCard}>
          <div className={s.sizeHead}><b>{x.d} · {x.name}</b><small>{x.line}</small></div>
          <div className={s.sizeRow}>
            {[1, 2, 3, 4].map((n) => (
              <div key={n} className={s.sizeCell} style={{ color: "#b06cff" }}>
                <span className={s.sizeArt}><SizeArt d={x.d} n={n} /></span>
                <small>{SIZE_NAMES[n - 1]}</small>
              </div>
            ))}
          </div>
          {/* on the Map row, as it sits there (Stadium, Large) */}
          <div className={s.sizeOnRow}>
            <span className={s.sizeOnIcon}><svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="#b06cff" strokeWidth="2"><rect x="3" y="6" width="18" height="12" rx="6" /></svg></span>
            <b>Map</b>
            <span className={s.sizeOnArt} style={{ color: "#b06cff" }}>
              <svg viewBox="0 0 60 32" height="30" fill="none"><rect x="2" y="2" width="56" height="28" rx="14" stroke="currentColor" strokeWidth="2.4" /></svg>
              <SizeArt d={x.d} n={3} />
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}

// ---------- the sheet ----------
function Both({ children }: { children: () => React.ReactNode }) {
  return (
    <div className={s.pair}>
      <div><span className={s.frameLabel}>PC</span><div className={s.pc}>{children()}</div></div>
      <div><span className={s.frameLabel}>Phone, sideways (844 x 390)</span><Land.Provider value><div className={s.phone}>{children()}</div></Land.Provider></div>
    </div>
  );
}
export default function RoomDesignsSheet() {
  return (
    <div className={s.page}>
      <h1>A map's size: five ways to draw it</h1>
      <SizeLab />
      <h1 className={s.h1b}>Game Rooms: rules as rows you press</h1>
      <p className={s.lede}>The old room's layout. Each rule shows its value; press it to change it. The rows work. E, F and G draw the value; A to D are the first round.</p>
      <section>
        <h2>Before · the room as it was until 4 October (pictures of the old build)</h2>
        <div className={s.before}>
          <figure><img src="/debug/rooms-before-top.jpg" alt="The old room, top" /><figcaption>The room: players left, every rule laid out in the middle, invite and chat right</figcaption></figure>
          <figure><img src="/debug/rooms-before-bottom.jpg" alt="The old room, scrolled" /><figcaption>Scrolled: game mode, spawn rate, power-ups</figcaption></figure>
          <figure><img src="/debug/rooms-before-local.jpg" alt="The old local match setup" /><figcaption>Local match: the same middle column</figcaption></figure>
        </div>
      </section>
      <h1>E, as the old room's middle (press a row: its choice drops down under it)</h1>
      <section><h2>E1 · E as it was: the drawing, then the words</h2><Both>{() => <OldRoom eStyle="E1" />}</Both></section>
      <section><h2>E2 · Taller rows: the name small over the value, the drawing large</h2><Both>{() => <OldRoom eStyle="E2" />}</Both></section>
      <section><h2>E3 · Two groups under the old headings: the arena, the power-ups</h2><Both>{() => <OldRoom eStyle="E3" />}</Both></section>
      <section><h2>E4 · The value as a pill in the rule's colour</h2><Both>{() => <OldRoom eStyle="E4" />}</Both></section>
      <section><h2>E5 · Speed and spawn rate show every choice, the chosen one lit</h2><Both>{() => <OldRoom eStyle="E5" />}</Both></section>
      <h1 className={s.h1b}>The room</h1>
      <section><h2>H · The old room, the left column simpler: one Add player, one Autofill button (both open a choice)</h2><Both>{() => <OldRoom />}</Both></section>
      <section><h2>S3 · Creating a room: only who can join, then the room</h2><Both>{() => <Stepper v="S3" />}</Both></section>
      <section><h2>E · Rows, each value drawn on the right</h2><Both>{() => <Room v="E" />}</Both></section>
      <section><h2>F · The value large, two to a line, the game mode in its colour</h2><Both>{() => <Room v="F" />}</Both></section>
      <section><h2>G · The game mode as a banner on top, rows beneath</h2><Both>{() => <Room v="G" />}</Both></section>
      <h1 className={s.h1b}>First round</h1>
      <section><h2>A · A list of rows</h2><Both>{() => <Room v="A" />}</Both></section>
      <section><h2>B · Tiles, the value large</h2><Both>{() => <Room v="B" />}</Both></section>
      <section><h2>C · The map as a picture, rows beneath</h2><Both>{() => <Room v="C" />}</Both></section>
      <section><h2>D · One line of chips</h2><Both>{() => <Room v="D" />}</Both></section>
      <h1 className={s.h1b}>Creating a room</h1>
      <section><h2>S1 · Two steps: who can join, then the game</h2><Both>{() => <Stepper v="S1" />}</Both></section>
      <section><h2>S2 · One step: the kind on top, the rules as rows</h2><Both>{() => <Stepper v="S2" />}</Both></section>
    </div>
  );
}
